import { Button, Space } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { DetailHeader as ZhDetailHeader } from "zhihao-ui-react";

import { deliveryService } from "@/api/services/deliveryService";
import { areaLib } from "@/utils/areaLib";
import { confirmDialog } from "@/utils/confirmDialog";
import { dateTimeFormat } from "@/utils";
import { showRequestError } from "@/utils/requestErrorMessage";
import type { DeliveryVo, ReceivePayment } from "@zhihaoscm/entity";
import { DELIVERY_STATE, DELIVERY_TYPE } from "@zhihaoscm/meta";
import DeliveryDetail from "./tradeOrder/components/deliveryDetail";
import { TradeOrderDeliveryDialog } from "./tradeOrder/components/deliveryManagement/TradeOrderDeliveryDialog";
import "./TradeOrderDeliveryDetailPage.css";

/** 交易提货详情的加载契约，允许测试替换真实接口。 */
export interface TradeOrderDeliveryDetailPageProps {
    loadDetail?: (id: string) => Promise<DeliveryVo>;
}

/** 交易管理必须读取交易提货 VO，不能复用采购提货的 purchaseGoods 结构。 */
export async function loadTradeOrderDeliveryDetail(id: string): Promise<DeliveryVo> {
    return deliveryService.getDeliveryVo(id);
}

/** 将交易提货 VO 转成旧版 deliveryDetail 子组件使用的平铺字段。 */
function toDeliveryView(data: DeliveryVo) {
    const delivery = data.delivery;
    const address = delivery.addressInfo;
    const ship = delivery.shipInfo;
    const fullAddress = address?.province
        ? `${areaLib.getFullAreaName(address.province, address.city, address.district)}${address.detailAddress || ""}`
        : "--";
    return {
        state: Number(delivery.state),
        newDataFlag: delivery.newDataFlag,
        orderState: data.order?.state,
        scheduleDate: delivery.scheduleDate,
        settlementAmount: delivery.settlementAmount,
        estimatedTon: delivery.estimatedTon,
        settlementUnitAmount: delivery.settlementUnitAmount,
        transportOrderShipId: data.transportOrderShip?.id || delivery.transportOrderShipId,
        shipName: ship
            ? `${ship.cnname || ship.name}/${delivery.shipId || data.transportOrderShip?.shipId || ship.mmsi || ""}`
            : "--",
        orderId: delivery.orderId,
        scheduleId: delivery.scheduleId,
        orderType: delivery.productType === 1 ? "寄售商品" : delivery.productType === 2 ? "团购商品" : "捡漏商品",
        contacts: address?.contacts,
        cellphone: address?.cellphone,
        address: fullAddress,
        detailAddress: address?.detailAddress
    };
}

/** 将收款记录转换为提货收款页的平铺字段。 */
function toPaymentViews(payments: ReceivePayment[]) {
    return payments.map(payment => ({
        id: payment.id,
        amount: payment.amount,
        payTime: payment.payTime,
        paymentInstruction: payment.remark,
        state: payment.state
    }));
}

/** 交易管理提货详情页，保持 Vue 的 query 路由、页签和编辑弹窗行为。 */
export function TradeOrderDeliveryDetailPage({
    loadDetail = loadTradeOrderDeliveryDetail
}: TradeOrderDeliveryDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const deliveryId = query.get("id") || "";
    const [detail, setDetail] = useState<DeliveryVo>();
    const [editOpen, setEditOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const reload = async () => {
        if (!deliveryId) return;
        try {
            setDetail(await loadDetail(deliveryId));
            setError(null);
        } catch (requestError) {
            setError("提货详情加载失败");
            showRequestError(requestError, "提货详情加载失败");
        }
    };

    useEffect(() => {
        void reload();
        // query.id 是 Vue 旧版详情页的唯一数据依赖。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deliveryId, loadDetail]);

    const view = detail ? toDeliveryView(detail) : undefined;
    const state = Number(detail?.delivery?.state);
    const showPayHistory = [DELIVERY_STATE.CONFIRMED.code, DELIVERY_STATE.PICKED_UP.code].includes(state);
    const canEdit = [
        DELIVERY_STATE.TO_BE_INITIATED.code,
        DELIVERY_STATE.CONFIRMED.code,
        DELIVERY_STATE.PICKED_UP.code
    ].includes(state);
    const operate = async (action: "initiate" | "cancel" | "delete") => {
        if (!detail?.delivery?.id) return;
        const messages = {
            initiate: "是否向买家发起提货",
            cancel: "是否取消提货，取消后提货数据不可更改",
            delete: "是否删除提货，删除后该数据将会消失"
        };
        if (!(await confirmDialog(messages[action]))) return;
        try {
            if (action === "initiate") await deliveryService.initiateDelivery(detail.delivery.id);
            if (action === "cancel") await deliveryService.cancelDelivery(detail.delivery.id);
            if (action === "delete") {
                await deliveryService.deleteDelivery(detail.delivery.id);
                void navigate(-1);
                return;
            }
            await reload();
        } catch (requestError) {
            showRequestError(requestError, "提货操作失败");
        }
    };

    return (
        <section aria-label="trade-order-delivery-detail-page" className="detail-page-transparent flex min-h-full flex-col">
            {detail ? (
                <>
                    <div className="trade-order-delivery-detail-page__content">
                        <DeliveryDetail
                            data={view}
                            headerContent={
                                <ZhDetailHeader
                                    leftInfos={[
                                        { label: "提货编号", value: detail.delivery.id },
                                        { label: "提货类型", value: DELIVERY_TYPE.getIntroByCode(detail.delivery.type) }
                                    ]}
                                    rightInfos={[{ label: "创建时间", value: dateTimeFormat(detail.delivery.createdTime) }]}
                                    stateDesc={DELIVERY_STATE.getIntroByCode(detail.delivery.state) || "--"}
                                    title="提货详情"
                                />
                            }
                            materials={[]}
                            onCancel={() => void operate("cancel")}
                            onDelete={() => void operate("delete")}
                            onEdit={() => setEditOpen(true)}
                            onInitiate={() => void operate("initiate")}
                            payHistory={toPaymentViews(detail.receivePayments || [])}
                            showPayHistory={showPayHistory}
                        />
                    </div>
                    <Space className="operation-fixed-btn" size={0}>
                        <Button onClick={() => navigate(-1)}>返回</Button>
                    </Space>
                    {canEdit && detail.order ? (
                        <TradeOrderDeliveryDialog
                            editingDelivery={detail}
                            open={editOpen}
                            order={detail.order}
                            onCancel={() => setEditOpen(false)}
                            onSaved={async () => {
                                setEditOpen(false);
                                await reload();
                            }}
                        />
                    ) : null}
                </>
            ) : error ? (
                <div role="alert">{error}</div>
            ) : null}
        </section>
    );
}

export default TradeOrderDeliveryDetailPage;
